import React, { createContext, useContext, useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { adminApi } from '../services/api';
import { clearSessionState } from '../utils/sessionStorage';
import { clearAllAppData } from '../utils/token';
import { setCurrentUser, clearCurrentUser } from '../components/redux/slices/userSlice';
import { loadUserPreferences, clearUserPreferences } from '../components/redux/slices/userPreferencesSlice';
import { persistor } from '../components/redux/store';
import { extractScopesFromToken, hasPermission, canPerformMethod } from '../utils/scopeUtils';

const AuthContext = createContext();

// Helper function to decode JWT without verification (client-side)
const decodeJWT = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const dispatch = useDispatch();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [alertState, setAlertState] = useState({ show: false, type: 'info', title: '', message: '' });
  const [activeHealthSystem, setActiveHealthSystemState] = useState(null);
  const [accessibleHealthSystems, setAccessibleHealthSystems] = useState([]);
  const [activeOrganization, setActiveOrganizationState] = useState(null);
  const [accessibleOrganizations, setAccessibleOrganizations] = useState([]);
  const [userScopes, setUserScopes] = useState([]); // OAuth2 scopes from JWT
  
  // Function to check if current token is valid
  const isTokenValid = () => {
    try {
      const adminApiToken = localStorage.getItem('admin_api_token');
      const storedTokens = localStorage.getItem('admin_tokens');
      
      if (!adminApiToken || !storedTokens) {
        return false;
      }      
      const parsedTokens = JSON.parse(storedTokens);      
      // Check if token has expired (with 5 minute buffer)
      if (parsedTokens.expires_at) {
        const expirationTime = new Date(parsedTokens.expires_at);
        const currentTime = new Date();
        const bufferTime = 5 * 60 * 1000; // 5 minutes in milliseconds        
        return expirationTime.getTime() > (currentTime.getTime() + bufferTime);
      }
      return true;
    } catch (err) {
      return false;
    }
  };
  
  useEffect(() => {
    // Check if user is logged in on component mount
    const checkAuth = async () => {
      try {
        // Check if there's an admin API token in localStorage
        const adminApiToken = localStorage.getItem('admin_api_token');
        const storedTokens = localStorage.getItem('admin_tokens');

        if (adminApiToken && storedTokens) {
          const parsedTokens = JSON.parse(storedTokens);

          // Check if the token is valid (not expired)
          if (isTokenValid()) {
            // Decode JWT to get fresh metadata (including health_systems)
            const jwtPayload = decodeJWT(adminApiToken);

            // Prefer JWT payload values over localStorage
            const tokenType = jwtPayload?.type || parsedTokens.token_type;
            const healthSystemId = jwtPayload?.health_system_id || parsedTokens.health_system_id;
            const healthcareSystems = jwtPayload?.health_systems || parsedTokens.health_systems;

            // Determine user type based on token type
            const isSalesUser = tokenType === 'core_server_sales_user';
            const isAnkrAdmin = tokenType === 'core_server_admin' || tokenType === 'core_server_ankr_user' || tokenType === 'core_server_general';
            const isHealthcareUser = tokenType === 'core_server_healthcare_user' || tokenType === 'core_server_healthcare_provider';

            // Default to patient user if no other role matches (base permission)
            const isPatientUser = tokenType === 'core_server_patient_user' ||
                                  (!isSalesUser && !isAnkrAdmin && !isHealthcareUser);

            // Extract OAuth2 scopes from JWT
            const scopes = extractScopesFromToken(adminApiToken);
            setUserScopes(scopes);

            fetchUserProfile(tokenType, jwtPayload);
            setUser({ id: jwtPayload?.core_server_user_id || jwtPayload?.api_token_id || parsedTokens.api_token_id, name: 'Admin User', role: 'admin', tokenType: tokenType, isAnkrAdmin: isAnkrAdmin, isSalesUser: isSalesUser, isHealthcareUser: isHealthcareUser, isPatientUser: isPatientUser, username: jwtPayload?.username || jwtPayload?.sub || null, email: jwtPayload?.email || null, first_name: jwtPayload?.first_name || null, last_name: jwtPayload?.last_name || null, organization_id: jwtPayload?.organization_id || null, organization_name: jwtPayload?.organization_name || null, team_id: jwtPayload?.team_id || null, team_name: jwtPayload?.team_name || null, health_system_id: jwtPayload?.health_system_id || null, health_systems: jwtPayload?.health_systems || null, aud: jwtPayload?.aud || null, iat: jwtPayload?.iat || null, exp: jwtPayload?.exp || null, scopes: scopes });
            fetchHealthSystemData(tokenType, healthSystemId, healthcareSystems);
            fetchOrganizationData(tokenType, jwtPayload?.aud);

            // Load user preferences from backend into Redux
            dispatch(loadUserPreferences());
          } else {
            // Token has expired, clear it
            localStorage.removeItem('admin_api_token');
            localStorage.removeItem('admin_tokens');
            setUser(null);
          }
        } else {
          setUser(null);
        }
      } catch (err) {
        // Clear potentially corrupted tokens
        localStorage.removeItem('admin_api_token');
        localStorage.removeItem('admin_tokens');
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
    
    // Set up periodic token validation (every 5 minutes)
    const tokenCheckInterval = setInterval(() => {
      if (user && !isTokenValid()) {
        // Token has expired, clear user state
        localStorage.removeItem('admin_api_token');
        localStorage.removeItem('admin_tokens');
        setUser(null);
        // Save current path for post-login redirect
        const currentPath = window.location.pathname + window.location.search;
        if (currentPath !== '/admin/login') {
          localStorage.setItem('redirect_after_login', currentPath);
        }
        // Redirect to login
        window.location.href = '/admin/login?session_expired=true';
      }
    }, 5 * 60 * 1000); // Check every 5 minutes
    
    return () => clearInterval(tokenCheckInterval);
  }, []);
  
  // Fetch health system data based on token type and optional health_system_id.
  // Admin/general/ankr_user tokens: fetch all accessible health systems.
  // Scoped user tokens (healthcare_user, healthcare_provider): use health_systems from JWT if available, else fetch.
  const fetchHealthSystemData = async (tokenType, healthSystemId, healthcareSystems) => {
    try {
      if (tokenType === 'core_server_admin' || tokenType === 'core_server_general' || tokenType === 'core_server_ankr_user') {
        const response = await adminApi.getHealthSystems();
        if (response.data?.success) {
          const systems = response.data.health_systems || [];
          setAccessibleHealthSystems(systems);
          if (systems.length > 0) setActiveHealthSystemState(systems[0]);
        }
      } else if (healthcareSystems && Array.isArray(healthcareSystems) && healthcareSystems.length > 0) {
        // Use health_systems from JWT (OAuth2 multi-tenancy)
        setAccessibleHealthSystems(healthcareSystems);
        setActiveHealthSystemState(healthcareSystems[0]);
      } else if (healthSystemId) {
        // Fallback: single health system from old tokens
        const response = await adminApi.getHealthSystemDetails(healthSystemId);
        const hs = response.data?.health_system || response.data;
        if (hs) {
          setAccessibleHealthSystems([hs]);
          setActiveHealthSystemState(hs);
        }
      }
    } catch (err) {
      // Non-fatal — health system context is informational
      console.error('Failed to fetch health system data:', err);
    }
  };

  // Fetch organization data based on token type and optional organization IDs.
  // Admin/general/ankr_user tokens: fetch all accessible organizations.
  // Sales user tokens: use organizationIds from JWT 'aud' claim if available, else fetch all.
  // Healthcare users: skip organization fetch (they use health systems instead)
  const fetchOrganizationData = async (tokenType, organizationIds) => {
    // Healthcare users don't need sales organization data
    if (tokenType === 'core_server_healthcare_user' || tokenType === 'core_server_healthcare_provider' || tokenType === 'core_server_patient_user') {
      return;
    }

    try {
      const response = await adminApi.getOrganizations();
      if (response.data) {
        const allOrgs = Array.isArray(response.data) ? response.data : response.data.organizations || [];

        // Admin users get access to all organizations
        if (tokenType === 'core_server_admin' || tokenType === 'core_server_general' || tokenType === 'core_server_ankr_user') {
          setAccessibleOrganizations(allOrgs);
          if (allOrgs.length > 0) setActiveOrganizationState(allOrgs[0]);
        }
        // Sales users get filtered organizations based on JWT aud claim
        else if (organizationIds && Array.isArray(organizationIds) && organizationIds.length > 0) {
          const accessibleOrgs = allOrgs.filter(org => organizationIds.includes(org.id));
          setAccessibleOrganizations(accessibleOrgs);
          if (accessibleOrgs.length > 0) setActiveOrganizationState(accessibleOrgs[0]);
        }
      }
    } catch (err) {
      // Non-fatal — organization context is informational
      console.error('Failed to fetch organization data:', err);
    }
  };

  const setActiveHealthSystem = (healthSystem) => {
    setActiveHealthSystemState(healthSystem);
  };

  const setActiveOrganization = (organization) => {
    setActiveOrganizationState(organization);
  };

  const fetchUserProfile = async (tokenType, jwtPayload) => {
    try {
      const coreServerUserId = jwtPayload?.core_server_user_id;
      if (!coreServerUserId) return null;

      const userTypeMap = { 'core_server_admin': 'admin', 'core_server_ankr_user': 'ankr', 'core_server_general': 'general', 'core_server_healthcare_user': 'healthcare', 'core_server_patient_user': 'patient', 'core_server_sales_user': 'sales' };
      const userType = userTypeMap[tokenType];
      if (!userType || userType === 'admin' || userType === 'general') return null;

      const response = await adminApi.get(`/admin/users/${userType}/${coreServerUserId}/`);
      if (response.data?.success && response.data.user) {
        const userData = response.data.user;
        const healthSystemMemberships = response.data.health_system_memberships || [];

        dispatch(setCurrentUser({
          id: userData.id,
          djangoUserId: userData.user?.id || null,
          username: userData.user?.username || userData.username || '',
          email: userData.user?.email || userData.email || '',
          firstName: userData.user?.first_name || userData.first_name || '',
          lastName: userData.user?.last_name || userData.last_name || '',
          tokenType: tokenType,
          userType: userType,
          isAnkrAdmin: tokenType === 'core_server_admin' || tokenType === 'core_server_ankr_user' || tokenType === 'core_server_general',
          isSalesUser: tokenType === 'core_server_sales_user',
          isHealthcareUser: tokenType === 'core_server_healthcare_user',
          isPatientUser: tokenType === 'core_server_patient_user',
          role: userData.role?.name || '',
          department: userData.department || '',
          title: userData.title || userData.job_title || '',
          phone: userData.phone || '',
          phone2: userData.phone_2 || '',
          timezone: userData.timezone || '',
          teamId: jwtPayload?.team_id || '',
          teamName: jwtPayload?.team_name || '',
          organizationId: jwtPayload?.organization_id || '',
          organizationName: jwtPayload?.organization_name || '',
          healthSystemMemberships: healthSystemMemberships,
          isActive: userData.is_active !== false
        }));
        return userData;
      }
    } catch (err) {
      null
    }
    return null;
  };

  const login = async (credentials) => {
    try {
      const response = await adminApi.generateAdminToken(credentials.username, credentials.password);
      if (response.status === 201 && response.data.api_token) {
        const apiToken = response.data.api_token;
        localStorage.setItem('admin_api_token', apiToken);
        const jwtPayload = decodeJWT(apiToken);
        const tokenType = jwtPayload?.type || response.data.token_type || response.data.api_token_type;
        const healthSystemId = jwtPayload?.health_system_id || response.data.health_system_id || null;
        const healthcareSystems = jwtPayload?.health_systems || response.data.health_systems || null;

        localStorage.setItem('admin_tokens', JSON.stringify({ api_token_id: jwtPayload?.api_token_id || response.data.api_token_id, expires_at: response.data.expires_at, token_type: tokenType, health_system_id: healthSystemId, health_systems: healthcareSystems }));

        const isSalesUser = tokenType === 'core_server_sales_user';
        const isAnkrAdmin = tokenType === 'core_server_admin' || tokenType === 'core_server_ankr_user' || tokenType === 'core_server_general';
        const isHealthcareUser = tokenType === 'core_server_healthcare_user' || tokenType === 'core_server_healthcare_provider';
        const isPatientUser = tokenType === 'core_server_patient_user' || (!isSalesUser && !isAnkrAdmin && !isHealthcareUser);

        // Extract OAuth2 scopes from JWT
        const scopes = extractScopesFromToken(apiToken);
        setUserScopes(scopes);

        await fetchUserProfile(tokenType, jwtPayload);

        const user = { id: jwtPayload?.core_server_user_id || jwtPayload?.api_token_id || response.data.api_token_id, name: credentials.username, role: 'admin', tokenType: tokenType, isAnkrAdmin: isAnkrAdmin, isSalesUser: isSalesUser, isHealthcareUser: isHealthcareUser, isPatientUser: isPatientUser, username: jwtPayload?.username || jwtPayload?.sub || null, email: jwtPayload?.email || null, first_name: jwtPayload?.first_name || null, last_name: jwtPayload?.last_name || null, organization_id: jwtPayload?.organization_id || null, organization_name: jwtPayload?.organization_name || null, team_id: jwtPayload?.team_id || null, team_name: jwtPayload?.team_name || null, health_system_id: jwtPayload?.health_system_id || null, health_systems: jwtPayload?.health_systems || null, aud: jwtPayload?.aud || null, iat: jwtPayload?.iat || null, exp: jwtPayload?.exp || null, scopes: scopes };

        setUser(user);
        fetchHealthSystemData(tokenType, healthSystemId, healthcareSystems);
        fetchOrganizationData(tokenType, jwtPayload?.aud);
        return { success: true };
      }
      return { success: false, message: 'Authentication failed' };
    } catch (err) {
      if (err.response) {
        switch (err.response.status) {
          case 403: return { success: false, message: 'Invalid username and/or password. Please try again.' };
          case 401: return { success: false, message: 'Invalid username and/or password. Please try again.' };
          case 500: return { success: false, message: 'Server error. Please try again later.' };
          default: return { success: false, message: err.response.data?.message || 'Authentication failed' };
        }
      }
      return { success: false, message: err.message || 'Network error. Please check your connection.' };
    }
  };
  
  const logout = async (redirectPath = null) => {
    try {
      // 1. Pause persistor to prevent any new persist operations during logout
      persistor.pause();

      // 2. Clear Redux state
      dispatch(clearCurrentUser());
      dispatch(clearUserPreferences());

      // 3. Purge redux-persist FIRST to properly clear all persisted state from localStorage
      await persistor.purge();

      // 4. Clear React state
      setUser(null);
      setAlertState({ show: false, type: 'info', title: '', message: '' });

      // 5. Clear localStorage (tokens, app data) - after Redux-persist purge
      clearAllAppData();

      // 6. Clear sessionStorage
      clearSessionState();
      sessionStorage.clear();

      // 7. Clear cookies
      document.cookie.split(";").forEach(function(c) {
        document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
      });

      // 8. Redirect with query parameter
      if (redirectPath) {
        window.location.href = `${redirectPath}?logout=success`;
      }
      return { success: true };
    } catch (err) {
      setAlertState({ show: true, type: 'error', title: 'Logout Failed', message: err.message || 'Unknown error. Please try again or clear your browser cache.' });
      return { success: false, error: err.message };
    }
  };
  
  return (
    <AuthContext.Provider value={{
      user, loading, login, logout, alertState, setAlertState,
      activeHealthSystem, accessibleHealthSystems, setActiveHealthSystem,
      activeOrganization, accessibleOrganizations, setActiveOrganization,
      userScopes, hasPermission, canPerformMethod
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hooks for using auth context
export const useAuth = () => {
  const { user, loading } = useContext(AuthContext);
  return !loading && user !== null;
};

export const useIsLoggedIn = () => {
  const { user, loading } = useContext(AuthContext);
  return !loading && user !== null;
};

export const useUserRole = () => {
  const { user } = useContext(AuthContext);
  return user?.role || null;
};

export const useIsAnkrAdmin = () => {
  const { user } = useContext(AuthContext);
  return user?.isAnkrAdmin || false;
};

export const useIsSalesUser = () => {
  const { user } = useContext(AuthContext);
  return user?.isSalesUser || false;
};

export const useIsHealthcareUser = () => {
  const { user } = useContext(AuthContext);
  return user?.isHealthcareUser || false;
};

export const useIsPatientUser = () => {
  const { user } = useContext(AuthContext);
  return user?.isPatientUser || false;
};

export const useHealthSystem = () => {
  const { activeHealthSystem, accessibleHealthSystems, setActiveHealthSystem } = useContext(AuthContext);
  return { activeHealthSystem, accessibleHealthSystems, setActiveHealthSystem };
};

export const useOrganization = () => {
  const { activeOrganization, accessibleOrganizations, setActiveOrganization } = useContext(AuthContext);
  return { activeOrganization, accessibleOrganizations, setActiveOrganization };
};

export const useAuthContext = () => useContext(AuthContext);

// Scope-related hooks
export const useUserScopes = () => {
  const { userScopes } = useContext(AuthContext);
  return userScopes || [];
};

export const useHasPermission = () => {
  const { userScopes, hasPermission: checkPermission } = useContext(AuthContext);
  return (resource, operation) => checkPermission(userScopes, resource, operation);
};

export const useCanPerformMethod = () => {
  const { userScopes, canPerformMethod: checkMethod } = useContext(AuthContext);
  return (resource, method) => checkMethod(userScopes, resource, method);
};